import {useCallback, useEffect, useMemo} from 'react'
import {useFormContext} from 'react-hook-form'

import {useDrawer} from '../../../components/Drawer/hooks/useDrawer'
import {useAddressActionsContext} from '../../../hooks/useAddressActionsContext'
import {useAddressContext} from '../../../hooks/useAddressContext'
import {AddressWidgetConfig} from '../../types'

type UseAddressWidgetKeysReturn = {
  /**
   * Закрытие `AddressDrawer` с формой настройки точки заказа
   */
  handleClose: VoidFunction
  /**
   * Открытие `AddressDrawer` с формой настройки точки заказа
   */
  handleOpen: VoidFunction
  /**
   * Обработчик отправки формы точки заказа:
   * - Если не `isFinalRoutePoint` - открытие формы настройки следующей точки заказа
   * - Если `isFinalRoutePoint` - закрытие `AddressDrawer`
   */
  handleSubmit: VoidFunction
  /**
   * Флаг, указан ли адрес в форме настройки точки заказа
   */
  isAddressSelected: boolean
  /**
   * Флаг, открыта ли форма настройки последней точки заказа
   */
  isFinalRoutePoint: boolean
  /**
   * Флаг, открыта ли форма настройки точки заказа
   */
  opened: boolean
}

/**
 * Возвращает коллбеки и boolean-флаги для работы с `AddressWidget`
 * @param props конфигурация виджета `AddressWidget`
 * @returns коллбеки и boolean-флаги для работы с `AddressWidget`
 */
export const useAddressWidget = (props: AddressWidgetConfig): UseAddressWidgetKeysReturn => {
  const {watch} = useFormContext()
  const address = watch(`${props.widget_id}.address`)

  const {open, close, opened} = useDrawer()
  const {addressIndexes, currentIndex} = useAddressContext()
  const {addAddress, changeCurrentIndex, removeAddress} = useAddressActionsContext()

  const isAddressSelected = useMemo(() => Boolean(address), [address])

  const isFinalRoutePoint = useMemo(
    () => addressIndexes.indexOf(props.route_point_index) === addressIndexes.length - 1,
    [addressIndexes, props.route_point_index],
  )

  const handleOpen = useCallback(() => {
    changeCurrentIndex(props.route_point_index)
  }, [changeCurrentIndex, props.route_point_index])

  const handleClose = useCallback(() => {
    changeCurrentIndex(null)
  }, [changeCurrentIndex])

  const handleSubmit = useCallback(() => {
    if (isFinalRoutePoint) {
      handleClose()
      return
    }

    changeCurrentIndex(props.route_point_index + 1)
  }, [changeCurrentIndex, handleClose, isFinalRoutePoint, props.route_point_index])

  useEffect(() => {
    addAddress(props.route_point_index)

    return () => {
      removeAddress(props.route_point_index)
    }
  }, [addAddress, props.route_point_index, removeAddress])

  useEffect(() => {
    if (currentIndex === null) {
      close()
      return
    }

    if (currentIndex !== props.route_point_index) {
      close()
      return
    }

    open()
  }, [close, currentIndex, open, props.route_point_index])

  return {handleClose, handleOpen, handleSubmit, isAddressSelected, isFinalRoutePoint, opened}
}
